import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { NextUI } from '@/components/NextUI';
import Icon from '@/components/Common/Icons/Icon';
import clsx from 'clsx';

// ✅ Correct worker src
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FilePreviewPanelProps {
  file: File | null;
  processedFile?: File | null;
  className?: string;
  compactLayout?: boolean;
  extension: string; // sadece UI / badge için
}

const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({
  file,
  processedFile,
  className,
  compactLayout = false,
  extension,
}) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [previewWidth, setPreviewWidth] = useState(0);

  const getFileIcon = (value: string) => {
    switch (value) {
      case 'doc':
      case 'docx':
        return 'Word';
      case 'xls':
      case 'xlsx':
        return 'Excel';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'JpegToPdf24';
      default:
        return 'Pdf';
    }
  };

  const fileToDisplay = processedFile || file;

  if (!fileToDisplay) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-400">
        No document
      </div>
    );
  }

  const { type, name, size } = fileToDisplay;

  // 🔑 Preview logic → MIME TYPE
  const isPdf = type === 'application/pdf';
  const isImage = type.startsWith('image/');
  const isZip = [
    'application/zip',
    'application/x-zip-compressed',
    'application/x-7z-compressed',
    'application/x-rar-compressed',
  ].includes(type);

  // 🔑 Extension sadece UI için
  const finalExtension = extension?.toLowerCase().replace(/^\./, '');

  const extensionColors: Record<string, string> = {
    pdf: 'bg-[#F43F3F]', // kırmızı
    docx: 'bg-[#1E3A8A]', // lacivert
    xlsx: 'bg-[#16A34A]', // yeşil
    pptx: 'bg-[#EA580C]', // turuncu
    jpeg: 'bg-[#8B5CF6]', // mor
    jpg: 'bg-[#8B5CF6]', // mor
    png: 'bg-[#8B5CF6]', // mor
  };

  const bgColor = extensionColors[finalExtension] ?? 'bg-gray-400';

  // 🖼 Image preview URL
  const imageUrl = useMemo(() => {
    return isImage ? URL.createObjectURL(fileToDisplay) : '';
  }, [fileToDisplay, isImage]);

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!previewRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const element = previewRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) return;

      setPreviewWidth(entry.contentRect.width);
    });

    observer.observe(element);
    setPreviewWidth(element.clientWidth);

    return () => observer.disconnect();
  }, []);

  const previewPageWidth = compactLayout
    ? Math.min(Math.max(previewWidth - 48, 180), 220)
    : 300;

  return (
    <div className={`w-full h-full flex flex-col ${className}`}>
      {/* ================= HEADER ================= */}
      <div
        className={clsx(
          'flex w-full items-center rounded-lg border border-[#ECECEC] bg-[#E3FFF6]',
          compactLayout ? 'mb-6 h-[72px] px-5 py-4' : 'mb-8 h-[77px] px-6 py-[20px]'
        )}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0ZM13.774 8.13327C14.1237 7.70582 14.0607 7.0758 13.6332 6.72607C13.2058 6.37635 12.5758 6.43935 12.226 6.86679L8.42576 11.5116L7.20711 10.2929C6.81658 9.9024 6.18342 9.9024 5.79289 10.2929C5.40237 10.6834 5.40237 11.3166 5.79289 11.7071L7.79289 13.7071C7.99267 13.9069 8.26764 14.0129 8.54981 13.9988C8.83199 13.9847 9.09505 13.8519 9.27396 13.6333L13.774 8.13327Z"
            fill="#00CE8F"
          />
        </svg>

        <span className="text-dark font-medium text-[16px] md:text-[18px] flex-1 ml-4 truncate">
          Your document is ready!
        </span>

        <span
          className={`text-white text-[14px] md:text-[17px] px-4 py-2 rounded-[6px] font-semibold uppercase ${bgColor}`}
        >
          {finalExtension}
        </span>
      </div>

      {/* ================= PREVIEW ================= */}
      <div
        ref={previewRef}
        className={clsx(
          'relative flex w-full items-center justify-center overflow-hidden',
          compactLayout ? 'min-h-[260px]' : 'min-h-[300px]',
          className ? 'flex-1' : 'h-full flex-1'
        )}
      >
        {/* PDF */}
        {isPdf && (
          <div
            className={clsx(
              'flex h-full w-full items-center justify-center overflow-auto',
              compactLayout ? 'p-2' : 'p-4'
            )}
          >
            <Document
              file={fileToDisplay}
              loading={<NextUI.Spinner></NextUI.Spinner>}
              error={
                <div className="text-center text-red-500">
                  Failed to load PDF
                </div>
              }
            >
              <Page
                pageNumber={1}
                width={previewPageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="!z-0 max-w-full overflow-hidden h-auto shadow-lg !rounded-[20px] !border !border-[#EBECF1]"
              />
            </Document>
          </div>
        )}

        {/* IMAGE */}
        {isImage && (
          <img
            src={imageUrl}
            alt={name}
            className={clsx(
              'max-w-full object-contain shadow-lg !z-0 rounded-[20px] border border-[#EBECF1]',
              compactLayout ? 'max-h-[320px] p-2' : 'max-h-[400px] p-4'
            )}
          />
        )}

        {/* FALLBACK */}
        {!isPdf && !isImage && (
          <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="flex h-[112px] w-[112px] items-center justify-center rounded-[24px] border border-[#EBECF1] bg-white shadow-sm">
              <Icon
                name={getFileIcon(finalExtension)}
                width={56}
                height={56}
              />
            </div>
            <p className="text-[15px] text-[#6D6D6D]">
              Preview ready. Conversion will start after access is granted.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilePreviewPanel;
